import React, { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import PageHeader from "../../shared/components/PageHeader";

const termLabel = (term) =>
  term ? `${term.name}${term.is_semester_term ? " (semester)" : ""}` : "";

// Compact chip styling for the term select beside the title: no fixed width (it
// sizes to the selected term), tighter padding/font than size="small", and the
// arrow pulled to the edge with the right padding set to just clear it.
const compactSelectSx = {
  flexShrink: 0,
  fontSize: 13,
  "& .MuiSelect-select": { py: 0.5, pl: 1, pr: "22px" },
  "& .MuiSelect-icon": { right: 0 },
};

// Quick Find field: the in-progress text is local (a form draft kept off the
// URL); it is applied on submit/clear, when v1 writes the query param. Keyed by
// the applied query in AssignmentsHeader so an external reset reseeds it.
function AssignmentsSearchField({ query, onSearch }) {
  const [value, setValue] = useState(query ?? "");

  const submit = (e) => {
    e.preventDefault();
    onSearch(value.trim());
  };

  const clear = () => {
    setValue("");
    onSearch("");
  };

  return (
    <form onSubmit={submit}>
      <TextField
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Quick Find"
        size="small"
        sx={{ width: { xs: "100%", md: 220 } }}
        slotProps={{
          input: {
            "aria-label": "Quick Find",
            endAdornment: (
              <InputAdornment position="end">
                {value ? (
                  <IconButton
                    aria-label="Clear search"
                    onClick={clear}
                    edge="end"
                  >
                    <ClearRoundedIcon />
                  </IconButton>
                ) : (
                  <IconButton aria-label="Search" type="submit" edge="end">
                    <SearchRoundedIcon />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          },
        }}
      />
    </form>
  );
}

// Page-header definition for the legacy v1 Assignments page. Its three controls -
// Quick Find, the term picker, and the count (with its show-all/my toggle) - move
// into the right-aligned actions slot here. Data + callbacks cross the v1/v2
// bridge; v1 keeps owning the term/query params and the assignment fetch they
// drive.
export default function AssignmentsHeader({
  query,
  onSearch,
  terms = [],
  selectedTermId,
  onTermChange,
  manageAllPermission,
  onlyMyAssignments,
  myAssignmentsCount,
  allAssignmentsCount,
  showShowAllToggle,
  onToggleShowAll,
}) {
  const termValue = terms.some((t) => t.id === selectedTermId)
    ? selectedTermId
    : "";

  return (
    <PageHeader
      title={
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}
        >
          <Typography
            sx={{ fontSize: 24, lineHeight: 1.2, whiteSpace: "nowrap" }}
          >
            Assignments
          </Typography>
          <Select
            size="small"
            value={termValue}
            onChange={(e) => onTermChange(e.target.value)}
            displayEmpty
            inputProps={{ "aria-label": "Term" }}
            renderValue={(v) =>
              termLabel(terms.find((t) => t.id === v)) || "Select term"
            }
            sx={compactSelectSx}
          >
            {terms.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {termLabel(t)}
              </MenuItem>
            ))}
          </Select>
        </Box>
      }
      actions={
        <Stack
          direction="row"
          spacing={2}
          sx={{ alignItems: "center", justifyContent: "flex-end" }}
        >
          {showShowAllToggle && (
            <Button
              onClick={onToggleShowAll}
              size="small"
              sx={{
                flexShrink: 0,
                whiteSpace: "nowrap",
                textTransform: "none",
              }}
            >
              {onlyMyAssignments ? "Show all" : "Show my assignments"}
            </Button>
          )}

          <Box sx={{ whiteSpace: "nowrap", flexShrink: 0, fontSize: 14 }}>
            {manageAllPermission && (
              <>
                <Typography component="span" sx={{ fontWeight: 500 }}>
                  {onlyMyAssignments ? myAssignmentsCount : allAssignmentsCount}
                </Typography>{" "}
                of{" "}
              </>
            )}
            <Typography component="span" sx={{ fontWeight: 500 }}>
              {allAssignmentsCount}
            </Typography>{" "}
            assignments
          </Box>

          <AssignmentsSearchField
            key={query}
            query={query}
            onSearch={onSearch}
          />
        </Stack>
      }
    />
  );
}
